import { useQuery } from '@tanstack/react-query';
import { IOperationResult } from '../../extViewmodels';
import { BASE_URL } from '../constants';
import { IPublicNewsletterView } from '../models/Api';

const BASE_QUERY_KEY = `/public/newsletter/<code>`;

export class PublicNewsletterNotFoundError extends Error {
	constructor() {
		super('Public newsletter not found');
		this.name = 'PublicNewsletterNotFoundError';
	}
}

export function usePublicNewsletterQuery({ publicShareCode }: { publicShareCode: string }) {
	return useQuery({
		queryKey: [BASE_QUERY_KEY, publicShareCode],
		queryFn: async (): Promise<IPublicNewsletterView> => {
			const response = await fetch(`${BASE_URL}/public/newsletter/${encodeURIComponent(publicShareCode)}`, {
				credentials: 'omit',
				headers: {
					'Content-Type': 'application/json',
				},
			});
			if (response.status === 404 || response.status === 410) {
				throw new PublicNewsletterNotFoundError();
			}
			if (!response.ok) {
				throw new Error(`Failed to load newsletter (status ${response.status})`);
			}
			const result: IOperationResult<IPublicNewsletterView> = await response.json();
			if (!result.success || !result.value) {
				throw new Error(result.systemMessage || 'Failed to load newsletter');
			}
			return result.value;
		},
		enabled: Boolean(publicShareCode),
		retry: (failureCount, error) => {
			if (error instanceof PublicNewsletterNotFoundError) {
				return false;
			}
			return failureCount < 2;
		},
	});
}
